import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Breadcrumbs,
  DemenagementLayout,
  DictimmoCta,
  makeBreadcrumbs,
  SourceList,
} from "../demenagement/DemenagementCommon";
import guideContent from "./guideContent";
import "./GuidesPage.css";

const { guides, guideBySlug, sources } = guideContent;
const origin = "https://etatdulieu.com";
const guidePath = (guide) => `/guides/${guide.slug}`;
const categoryOrder = ["Avant de louer", "Avant d'acheter", "Vérifier une adresse", "Comprendre le prix d'un logement"];
const pathLabels = {
  "/": "Vérifier une adresse",
  "/demenagement": "Préparer un déménagement",
  "/cartes": "Explorer les cartes",
  "/checklists/checklist-visite-logement": "Checklist de visite",
  "/checklists/checklist-demenagement-pdf": "Checklist déménagement",
};

function articleJsonLd(guide, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.h1,
    description: guide.description,
    inLanguage: "fr-FR",
    mainEntityOfPage: `${origin}${path}`,
    author: { "@type": "Organization", name: "État du Lieu", url: origin },
    publisher: { "@type": "Organization", name: "État du Lieu", url: origin, logo: `${origin}/logo-blue-bg.png` },
  };
}

export function GuidesIndex() {
  const path = "/guides";
  const breadcrumbs = makeBreadcrumbs([{ name: "Guides pratiques", path }]);
  const listJsonLd = {
    "@context": "https://schema.org", "@type": "ItemList", name: "Guides pratiques État du Lieu",
    itemListElement: guides.map((guide, index) => ({
      "@type": "ListItem", position: index + 1, name: guide.h1, url: `${origin}${guidePath(guide)}`,
    })),
  };
  return (
    <DemenagementLayout
      title="Guides pratiques pour louer, acheter et vérifier une adresse — État du Lieu"
      description="Des méthodes concrètes pour vérifier un logement, son quartier, les risques, Internet et les prix avant une décision immobilière."
      path={path} breadcrumbs={breadcrumbs} jsonLd={[listJsonLd]}
      label="Guides pratiques" navigation="guides" includeDates={false}
    >
      <Breadcrumbs items={breadcrumbs} />
      <header className="move-tool-header guide-index-header">
        <h1>Guides pratiques pour choisir un logement</h1>
        <p>Une visite, une adresse, un prix demandé : partez de votre question et confrontez les données publiques à ce que vous voyez sur place.</p>
      </header>
      {categoryOrder.map((category) => (
        <section className="guide-category" key={category} aria-label={category}>
          <h2>{category}</h2>
          <div className="guide-card-grid">
            {guides.filter((guide) => guide.category === category).map((guide) => (
              <Link className="move-card-link" to={guidePath(guide)} key={guide.slug}>
                <span>{guide.h1}</span>
                <small>{guide.description}</small>
              </Link>
            ))}
          </div>
        </section>
      ))}
      <section className="move-note">
        <h2>Un outil à utiliser pendant la visite</h2>
        <p>La <Link to="/checklists/checklist-visite-logement">checklist de visite</Link> aide à noter l'état réel du logement. Pour les démarches après le choix du bien, retrouvez les outils de <Link to="/demenagement">déménagement</Link>.</p>
      </section>
    </DemenagementLayout>
  );
}

export function GuideArticle() {
  const { slug } = useParams();
  const guide = guideBySlug[slug];
  if (!guide) return <div className="move-page"><main className="move-shell"><h1>Guide introuvable</h1><Link to="/guides">Voir les guides</Link></main></div>;
  const path = guidePath(guide);
  const breadcrumbs = makeBreadcrumbs([{ name: "Guides pratiques", path: "/guides" }, { name: guide.h1, path }]);
  const related = guide.related.map((relatedPath) => ({
    path: relatedPath,
    label: guides.find((item) => guidePath(item) === relatedPath)?.h1 || pathLabels[relatedPath],
  })).filter((item) => item.label);
  return (
    <DemenagementLayout
      title={guide.title} description={guide.description} path={path}
      breadcrumbs={breadcrumbs} jsonLd={[articleJsonLd(guide, path)]}
      label={guide.category} navigation="guides" includeDates={false} ogType="article"
    >
      <Breadcrumbs items={breadcrumbs} />
      <article className="move-article guide-article">
        <header className="move-tool-header">
          <h1>{guide.h1}</h1>
          <p className="guide-direct-answer">{guide.answer}</p>
        </header>
        <div className="guide-article-body">
          {guide.sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </section>
          ))}
          {guide.example && (
            <section className="guide-example">
              <h2>{guide.example.heading}</h2>
              <p>{guide.example.body}</p>
            </section>
          )}
          <section>
            <h2>À faire avant de décider</h2>
            <ol>{guide.steps.map((step) => <li key={step}>{step}</li>)}</ol>
          </section>
          <section className="guide-report-box">
            <h2>Ce que vous pouvez vérifier avec une adresse</h2>
            <p>{guide.report.text}</p>
            <ul>{guide.report.checks.map((check) => <li key={check}>{check}</li>)}</ul>
          </section>
          <section>
            <h2>Ce qui reste à vérifier sur le bien</h2>
            <p>{guide.limits}</p>
          </section>
        </div>
      </article>
      <DictimmoCta
        title="Vérifier cette adresse gratuitement"
        text="Saisissez l'adresse du logement pour consulter les informations publiques pertinentes, puis confirmez les points décisifs sur place ou auprès des sources officielles."
        buttonLabel="Vérifier cette adresse gratuitement"
        eventName="guide_address_cta_started"
        eventData={{ slug: guide.slug, source: "guide" }}
      />
      <SourceList sources={guide.sourceKeys.map((key) => sources[key])} />
      <section className="guide-related no-print" aria-label="Pour aller plus loin">
        <h2>Pour aller plus loin</h2>
        <ul>
          {related.map((item) => <li key={item.path}><Link to={item.path}>{item.label}</Link></li>)}
          <li><Link to="/guides">Tous les guides pratiques</Link></li>
        </ul>
      </section>
    </DemenagementLayout>
  );
}
